import { supabase } from './supabase';

const SESSION_REFRESH_SKEW_MS = 2 * 60 * 1000;
const ONLINE_WAIT_MS = 5000;
const RECENT_SESSION_CHECK_MS = 15000;

let sessionRecoveryPromise = null;
let lastSession = null;
let lastSessionCheckedAt = 0;

export const noteAppWoke = () => {};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isSessionFreshEnough = (session) => {
  if (!session) return false;
  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : 0;
  return !expiresAtMs || expiresAtMs - Date.now() >= SESSION_REFRESH_SKEW_MS;
};

const isAuthLockContentionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('lock broken')
    || (message.includes('lock') && message.includes('steal'));
};

const runAuthOperation = async (operation, label, attempts = 3) => {
  let lastError;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (!isAuthLockContentionError(error) || attempt === attempts - 1) break;
      await delay(200 * (attempt + 1));
    }
  }

  const message = lastError?.message || `${label} failed`;
  const wrappedError = new Error(message);
  wrappedError.name = lastError?.name || 'AuthSessionError';
  wrappedError.cause = lastError;
  throw wrappedError;
};

export const waitForBrowserOnline = async (timeoutMs = ONLINE_WAIT_MS) => {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return true;

  await new Promise((resolve) => {
    const timeoutId = setTimeout(resolve, timeoutMs);
    const handleOnline = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('online', handleOnline);
      resolve();
    };
    window.addEventListener('online', handleOnline, { once: true });
  });

  return typeof navigator === 'undefined' || navigator.onLine !== false;
};

const recoverSession = async ({ force = false, label = 'Restoring session' } = {}) => {
  const online = await waitForBrowserOnline();
  if (!online) {
    const error = new Error('The portal is offline. Reconnect to the internet and try again.');
    error.name = 'NetworkOfflineError';
    throw error;
  }

  const { data: sessionData, error: sessionError } = await runAuthOperation(
    () => supabase.auth.getSession(),
    `${label} session check`
  );
  if (sessionError) throw sessionError;

  const session = sessionData?.session || null;
  lastSession = session;
  lastSessionCheckedAt = Date.now();
  if (!session) return null;

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : 0;
  const shouldRefresh = force || (expiresAtMs && expiresAtMs - Date.now() < SESSION_REFRESH_SKEW_MS);
  if (!shouldRefresh) return session;

  const { data: refreshData, error: refreshError } = await runAuthOperation(
    () => supabase.auth.refreshSession(),
    `${label} session refresh`
  );
  if (refreshError) throw refreshError;

  lastSession = refreshData?.session || session;
  lastSessionCheckedAt = Date.now();
  return lastSession;
};

export const ensureFreshSession = async ({ force = false, label = 'Restoring session' } = {}) => {
  if (!force
    && Date.now() - lastSessionCheckedAt < RECENT_SESSION_CHECK_MS
    && isSessionFreshEnough(lastSession)) {
    return lastSession;
  }

  if (sessionRecoveryPromise) return sessionRecoveryPromise;

  sessionRecoveryPromise = recoverSession({ force, label })
    .finally(() => {
      sessionRecoveryPromise = null;
    });

  return sessionRecoveryPromise;
};

export const isRecoverableSupabaseError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  const name = String(error?.name || '').toLowerCase();
  const status = Number(error?.status || error?.code || 0);

  return name.includes('abort')
    || name.includes('timeout')
    || message.includes('timed out')
    || message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('network request failed')
    || message.includes('load failed')
    || message.includes('lock broken')
    || (message.includes('lock') && message.includes('steal'))
    || message.includes('jwt expired')
    || message.includes('invalid jwt')
    || status === 401
    || status === 408
    || status === 429
    || status >= 500;
};
